import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class SearchService {
  async searchAll(query, filters = {}) {
    try {
      const response = await axios.get(`${API}/search`, {
        params: {
          q: query,
          ...filters
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Search failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Search failed' };
    }
  }

  async searchDoctors(query, filters = {}) {
    try {
      // Use new Kuwait doctors search endpoint
      const response = await axios.get(`${API}/doctors/search`, {
        params: {
          q: query,
          specialty: filters.speciality || filters.specialty,
          hospital: filters.hospital,
          limit: filters.limit || 20
        }
      });
      
      // Ensure we return doctors array in expected format
      const doctors = response.data.doctors || [];
      return { 
        success: true, 
        data: doctors 
      };
    } catch (error) {
      console.error('Doctor search failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Doctor search failed' };
    }
  }

  async searchClinics(query, filters = {}) {
    try {
      // Use new Kuwait healthcare providers search endpoint
      const response = await axios.get(`${API}/healthcare-providers/search`, {
        params: {
          q: query,
          category: filters.category,
          limit: filters.limit || 20
        }
      });
      
      // Ensure we return providers array in expected format
      const providers = response.data.providers || [];
      return { 
        success: true, 
        data: providers 
      };
    } catch (error) {
      console.error('Provider search failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Provider search failed' };
    }
  }

  async searchProviders(query, filters = {}) {
    try {
      // Use new Kuwait healthcare providers search endpoint
      const response = await axios.get(`${API}/healthcare-providers/search`, {
        params: {
          q: query,
          category: filters.category,
          limit: filters.limit || 20
        }
      });
      
      const providers = response.data.providers || [];
      return { 
        success: true, 
        data: providers 
      };
    } catch (error) {
      console.error('Provider search failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Provider search failed' };
    }
  }

  async getDoctors(filters = {}) {
    try {
      // Get all Kuwait doctors
      const response = await axios.get(`${API}/doctors`, {
        params: {
          specialty: filters.specialty,
          limit: filters.limit || 50
        }
      });
      
      const doctors = response.data.doctors || response.data || [];
      return { 
        success: true, 
        data: doctors 
      };
    } catch (error) {
      console.error('Get doctors failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get doctors' };
    }
  }

  async getHealthcareProviders(filters = {}) {
    try {
      // Get all Kuwait healthcare providers
      const response = await axios.get(`${API}/healthcare-providers`, {
        params: {
          category: filters.category,
          type: filters.type,
          area: filters.area,
          specialty: filters.specialty
        }
      });
      
      const providers = response.data.providers || [];
      return { 
        success: true, 
        data: providers 
      };
    } catch (error) {
      console.error('Get healthcare providers failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get providers' };
    }
  }

  async getDoctorsBySpecialty(specialty) {
    try {
      const response = await axios.get(`${API}/doctors/by-specialty/${encodeURIComponent(specialty)}`);
      const doctors = response.data.doctors || [];
      return { 
        success: true, 
        data: doctors 
      };
    } catch (error) {
      console.error('Get doctors by specialty failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get doctors by specialty' };
    }
  }

  async getDoctorsByHospital(hospitalName) {
    try {
      const response = await axios.get(`${API}/doctors/by-hospital/${encodeURIComponent(hospitalName)}`);
      const doctors = response.data.doctors || [];
      return { 
        success: true, 
        data: doctors 
      };
    } catch (error) {
      console.error('Get doctors by hospital failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get doctors by hospital' };
    }
  }

  async getDoctor(doctorId) {
    try {
      const response = await axios.get(`${API}/doctors/${doctorId}`);
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Get doctor failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get doctor' };
    }
  }

  async getHealthcareProvider(providerId) {
    try {
      const response = await axios.get(`${API}/healthcare-providers/${providerId}`);
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Get healthcare provider failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get provider' };
    }
  }

  async getSuggestions(query) {
    try {
      const response = await axios.get(`${API}/search/suggestions`, {
        params: { q: query }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get suggestions failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to get suggestions' };
    }
  }

  async getRecentSearches() {
    try {
      const searches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
      return { success: true, data: searches };
    } catch (error) {
      return { success: false, data: [] };
    }
  }

  saveRecentSearch(query, type) {
    try {
      const searches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
      const newSearch = { query, type, timestamp: new Date().toISOString() };
      
      // Remove duplicates and limit to 10 recent searches
      const filteredSearches = searches.filter(s => s.query !== query || s.type !== type);
      const updatedSearches = [newSearch, ...filteredSearches].slice(0, 10);
      
      localStorage.setItem('recent_searches', JSON.stringify(updatedSearches));
    } catch (error) {
      console.error('Failed to save recent search:', error);
    }
  }
}

export default new SearchService();